import React, { useState, useEffect } from 'react';
import { LayoutIcon, CaretDownIcon, PlusIcon, FilmSlateIcon, XIcon, CheckIcon, LightningIcon, CropIcon, PencilIcon } from '@phosphor-icons/react';
import { Project, Shot, GeneratorSelections } from '../types';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import { useProjectPermissions } from '../hooks/useProjectPermissions';
import { ProjectService } from '../lib/projectService';
import { ProductionBar, ProductionBarDivider } from './ProductionBar';
import { useNavigate } from 'react-router-dom';

interface StoryboardPageProps {
    onInject?: (data: { prompt: string, selections: Partial<GeneratorSelections>, shotId?: string }) => void;
}

export const StoryboardPage: React.FC<StoryboardPageProps> = ({ onInject }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { selectedProjectId, setSelectedProjectId, projects: globalProjects, projectsLoading } = useProject();
    const permissions = useProjectPermissions(selectedProjectId || undefined);
    const [activeScene, setActiveScene] = useState<string | null>(null);
    const [projectAspectRatio, setProjectAspectRatio] = useState("16:9");
    const [selectedShotId, setSelectedShotId] = useState<string | null>(null);
    const [isEditingLabel, setIsEditingLabel] = useState(false);
    const [tempLabel, setTempLabel] = useState("");

    // Extended project data with scenes/shots (we still need to fetch these)
    const [projects, setProjects] = useState<Project[]>([]);
    const [isLoadingProjects, setIsLoadingProjects] = useState(false);

    // Use global project ID
    const selectedProject = selectedProjectId || "";

    // Fetch Projects on Mount
    // Unified Fetch Function
    const fetchProjects = async () => {
        if (!user) return;
        setIsLoadingProjects(true);
        const { data, error } = await supabase
            .from('projects')
            .select(`
                *,
                scenes (
                    *,
                    shots ( * )
                )
            `)
            .order('created_at', { ascending: false });

        if (error) {
            console.error("Error fetching projects:", error);
        } else if (data) {
            const sorted = data.map((p: any) => ({
                ...p,
                scenes: p.scenes
                    .sort((a: any, b: any) => a.order_index - b.order_index)
                    .map((s: any) => ({
                        ...s,
                        shots: s.shots.sort((a: any, b: any) => a.order_index - b.order_index)
                    }))
            }));
            setProjects(sorted);
        }
        setIsLoadingProjects(false);
    };

    // Fetch on Mount
    useEffect(() => {
        fetchProjects();

        // Auto-select project if passed in URL
        if (typeof window !== 'undefined') {
            const hash = window.location.hash;
            const paramString = hash.split('?')[1];
            if (paramString) {
                const params = new URLSearchParams(paramString);
                const pid = params.get('project');
                if (pid) {
                    setSelectedProjectId(pid);
                }
            }
        }
    }, [user]);

    const activeProjectData = projects.find(p => p.id === selectedProject);
    const activeSceneData = activeProjectData?.scenes.find(s => s.id === activeScene);

    // Derived state for the active shot
    const activeShot = activeSceneData?.shots.find(s => s.id === selectedShotId);

    // -- CRUD HANDLERS --

    const handleCreateProject = async () => {
        if (!user) return;
        const { data, error } = await supabase.from('projects').insert({
            user_id: user.id,
            title: "Untitled Project",
            description: "New storyboard project"
        }).select().single();

        if (data) {
            await fetchProjects();
            setSelectedProjectId(data.id);
        }
    };

    const handleCreateScene = async () => {
        if (!selectedProject || !user) return;
        const project = projects.find(p => p.id === selectedProject);
        const newIndex = project ? project.scenes.length : 0;

        const { data, error } = await supabase.from('scenes').insert({
            project_id: selectedProject,
            slug: `SCENE ${newIndex + 1}`,
            description: "Describe the action...",
            order_index: newIndex
        }).select().single();

        if (data) {
            await fetchProjects();
            setActiveScene(data.id);
        }
    };

    const handleCreateShot = async () => {
        if (!activeScene || !user) return;
        const scene = activeSceneData;
        const newIndex = scene ? scene.shots.length : 0;

        const { data, error } = await supabase.from('shots').insert({
            scene_id: activeScene,
            shot_type: "Wide Shot",
            label: `Shot ${newIndex + 1}`,
            description: "Describe the shot...",
            order_index: newIndex,
            metadata: {
                characters: "None",
                location: "None",
                style: "None",
                lens: "None",
                angle: "None",
                lighting: "None",
                tod: "None"
            }
        }).select().single();

        if (data) {
            await fetchProjects();
        }
    };

    const updateShotMetadata = async (key: string, value: string) => {
        if (!selectedShotId) return;
        // Optimistic Update
        setProjects(prev => prev.map(p => {
            if (p.id !== selectedProject) return p;
            return {
                ...p,
                scenes: p.scenes.map(s => {
                    if (s.id !== activeScene) return s;
                    return {
                        ...s,
                        shots: s.shots.map(shot => {
                            if (shot.id !== selectedShotId) return shot;
                            return { ...shot, metadata: { ...shot.metadata, [key]: value } };
                        })
                    }
                })
            }
        }));

        const { error } = await supabase
            .from('shots')
            .update({
                metadata: { ...activeShot?.metadata, [key]: value } as any
            })
            .eq('id', selectedShotId);
    };

    const handleRenameShot = async (newLabel: string) => {
        if (!selectedShotId) return;
        setProjects(prev => prev.map(p => {
            if (p.id !== selectedProject) return p;
            return {
                ...p,
                scenes: p.scenes.map(s => {
                    if (s.id !== activeScene) return s;
                    return {
                        ...s,
                        shots: s.shots.map(shot => {
                            if (shot.id !== selectedShotId) return shot;
                            return { ...shot, label: newLabel };
                        })
                    }
                })
            }
        }));
        setIsEditingLabel(false);
        await supabase.from('shots').update({ label: newLabel }).eq('id', selectedShotId);
    };

    const handleDeleteShot = async (sceneId: string, shotId: string) => {
        const { error } = await supabase.from('shots').delete().eq('id', shotId);
        if (!error) {
            await fetchProjects();
            if (selectedShotId === shotId) setSelectedShotId(null);
        }
    };

    const handleInjectShot = () => {
        if (!activeShot) return;
        if (!onInject) { navigate(`/shot-designer?shot=${activeShot.id}`); return; }

        onInject({
            prompt: `${activeSceneData?.slug}. ${activeShot.description}`,
            selections: {
                subject: activeShot.metadata.characters,
                location: activeShot.metadata.location,
                style: activeShot.metadata.style,
                shot: activeShot.metadata.lens,
                angle: activeShot.metadata.angle,
                lighting: activeShot.metadata.lighting,
                tod: activeShot.metadata.tod,
                ratio: projectAspectRatio
            },
            shotId: activeShot.id
        });

        // Navigate to Generator/Lookbook logic might be needed here if this is a separate page now,
        // but for now we just call onInject which might bubble up to layout or context.
        // Actually, since it's a separate page, onInject needs to navigate.
        // We'll handle this in the parent or assume onInject does it.
    };

    return (
        <div className="flex flex-col w-full h-full bg-carbon">
            {/* Production Bar */}
            <ProductionBar page="storyboard">
                {/* New Project Button */}
                <button
                    onClick={handleCreateProject}
                    disabled={isLoadingProjects}
                    className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white transition-colors disabled:opacity-50"
                    title="New Project"
                >
                    <PlusIcon className="w-4 h-4" />
                </button>
            </ProductionBar>

            <div className="flex-1 p-8 pb-20">
                <div className="w-full max-w-7xl mx-auto flex flex-col gap-6">

                {selectedProject && activeProjectData ? (
                    <div className="bg-onyx border border-white/5 rounded-2xl overflow-hidden min-h-[600px] flex flex-col shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-500">

                        {/* Scene Navigation Bar */}
                        <div className="h-14 border-b border-white/5 bg-onyx flex items-center justify-between px-4 gap-4 flex-shrink-0">
                            {/* Scrollable Scenes (Left) */}
                            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide flex-1">
                                <FilmSlateIcon className="w-4 h-4 text-zinc-600 mr-2 flex-shrink-0" />
                                {activeProjectData.scenes.map((scene) => (
                                    <button
                                        key={scene.id}
                                        onClick={() => { setActiveScene(scene.id); setSelectedShotId(null); }}
                                        className={`
                                            px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all border
                                            ${activeScene === scene.id
                                                ? 'bg-purple-500/10 border-purple-500/20 text-purple-300'
                                                : 'bg-transparent border-transparent text-zinc-500 hover:text-zinc-300 hover:bg-white/5'}
                                        `}
                                    >
                                        <span className="opacity-50 mr-2">#{scene.order_index + 1}</span>
                                        {scene.slug}
                                    </button>
                                ))}
                                {permissions.canEdit && (
                                    <button
                                        onClick={handleCreateScene}
                                        className="w-8 h-8 rounded-full border border-dashed border-white/10 flex items-center justify-center text-zinc-600 hover:text-white hover:border-white/20 ml-2 flex-shrink-0"
                                    >
                                        <PlusIcon className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                            {/* Project Aspect Ratio (Right) */}
                            <div className="flex items-center gap-2 bg-onyx border border-white/10 rounded-lg px-3 py-1.5 flex-shrink-0">
                                <CropIcon className="w-3.5 h-3.5 text-zinc-500" />
                                <span className="text-xs text-zinc-400 font-medium mr-1 hidden sm:inline">Ratio:</span>
                                <select
                                    className="bg-transparent text-white text-xs font-bold outline-none cursor-pointer"
                                    value={projectAspectRatio}
                                    onChange={(e) => setProjectAspectRatio(e.target.value)}
                                >
                                    <option value="16:9">16:9</option>
                                    <option value="9:16">9:16</option>
                                    <option value="2.35:1">2.35:1</option>
                                    <option value="1:1">1:1</option>
                                </select>
                            </div>
                        </div>

                        {/* Main Workspace: Split View */}
                        <div className="flex-1 flex overflow-hidden">

                            {/* LEFT: Shot Grid */}
                            <div className="flex-1 bg-carbon relative overflow-y-auto p-8">
                                <div className="absolute inset-0 opacity-[0.02] pointer-events-none"
                                    style={{ backgroundImage: 'radial-gradient(circle, #808080 1px, transparent 1px)', backgroundSize: '24px 24px' }}
                                />

                                {/* Script Excerpt */}
                                <div className="mb-8 p-4 rounded-lg bg-white/5 border border-white/5 max-w-3xl">
                                    <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1">Current Scene Action</h4>
                                    <p className="font-mono text-zinc-300 text-sm">{activeSceneData?.description}</p>
                                </div>

                                {/* Shot Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 relative z-10 pb-20">
                                    {activeSceneData?.shots.map(shot => (
                                        <StoryboardCard
                                            key={shot.id}
                                            shotNumber={shot.order_index + 1}
                                            label={shot.label || `Shot ${shot.order_index + 1}`}
                                            type={shot.shot_type}
                                            desc={shot.description}
                                            img={shot.image_url || ''}
                                            active={selectedShotId === shot.id}
                                            onClick={() => {
                                                setSelectedShotId(shot.id);
                                                setIsEditingLabel(false);
                                            }}
                                            onDelete={permissions.canEdit ? () => handleDeleteShot(activeScene || '', shot.id) : undefined}
                                            onRenameClick={permissions.canEdit ? (e) => {
                                                e.stopPropagation();
                                                setSelectedShotId(shot.id);
                                                setTempLabel(shot.label || `Shot ${shot.order_index + 1}`);
                                                setIsEditingLabel(true);
                                            } : undefined}
                                        />
                                    ))}

                                    {/* Empty Slot - Only show when user can edit */}
                                    {permissions.canEdit && (
                                        <div
                                            onClick={handleCreateShot}
                                            className="aspect-video rounded-xl border-2 border-dashed border-zinc-800 bg-white/[0.01] hover:bg-white/[0.03] hover:border-zinc-700 transition-all cursor-pointer group flex flex-col items-center justify-center gap-3"
                                        >
                                            <div className="w-10 h-10 rounded-full bg-zinc-900 border border-white/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                                                <PlusIcon className="w-5 h-5 text-zinc-500 group-hover:text-purple-400" />
                                            </div>
                                            <span className="text-xs font-medium text-zinc-600 group-hover:text-zinc-400">Add Shot</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* RIGHT: Inspector Panel (Visible when shot selected) */}
                            {selectedShotId && activeShot && (
                                <div className="w-80 border-l border-white/5 bg-zinc-950 flex flex-col animate-in slide-in-from-right duration-300 z-20 shadow-2xl relative">
                                    <div className="h-14 border-b border-white/5 flex items-center justify-between px-4 bg-onyx">
                                        {isEditingLabel ? (
                                            <input
                                                autoFocus
                                                type="text"
                                                value={tempLabel}
                                                onChange={(e) => setTempLabel(e.target.value)}
                                                onBlur={() => handleRenameShot(tempLabel)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleRenameShot(tempLabel)}
                                                className="w-full bg-carbon/20 border border-purple-500/50 rounded px-2 py-1 text-xs font-bold text-white uppercase tracking-wider outline-none"
                                            />
                                        ) : (
                                            <div className="flex items-center gap-2 group/title cursor-pointer" onClick={() => {
                                                setTempLabel(activeShot.label || `Shot ${activeShot.order_index + 1}`);
                                                setIsEditingLabel(true);
                                            }}>
                                                <span className="text-xs font-bold text-zinc-300 uppercase tracking-wider truncate max-w-[180px]">
                                                    {activeShot.label || `Shot ${activeShot.order_index + 1}`} Inspector
                                                </span>
                                                <PencilIcon className="w-3 h-3 text-zinc-600 group-hover/title:text-white transition-colors" />
                                            </div>
                                        )}
                                        <button onClick={() => setSelectedShotId(null)} className="text-zinc-500 hover:text-white">
                                            <XIcon className="w-4 h-4" />
                                        </button>
                                    </div>

                                    <div className="flex-1 overflow-y-auto p-4 space-y-6">
                                        {/* Action Button */}
                                        <button
                                            onClick={handleInjectShot}
                                            className="w-full py-2.5 bg-zinc-100 hover:bg-white text-black border border-transparent rounded-md text-xs font-semibold shadow-[0_2px_10px_rgba(255,255,255,0.1)] flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                                        >
                                            <LightningIcon className="w-3.5 h-3.5 fill-black" />
                                            <span>{onInject ? 'Inject Selection' : 'Open in Shot Designer'}</span>
                                        </button>

                                        {/* Metadata Fields */}
                                        <div className="space-y-4">
                                            <div className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest mb-2 border-b border-white/5 pb-1">Shot Metadata</div>

                                            <MultiSelectMetadataField
                                                label="Characters"
                                                value={activeShot.metadata.characters}
                                                onChange={(v) => updateShotMetadata('characters', v)}
                                                options={["Cyber Samurai", "Ethereal Elf", "Space Marine", "Noir Detective", "Rebel Pilot", "Android Butler"]}
                                                max={3}
                                            />
                                            <MetadataField label="Location" value={activeShot.metadata.location} onChange={(v) => updateShotMetadata('location', v)} options={["None", "Neon City", "Mars Base", "Ancient Ruins", "Enchanted Forest"]} />
                                            <MetadataField label="Style" value={activeShot.metadata.style} onChange={(v) => updateShotMetadata('style', v)} options={["None", "Cinematic", "Anime", "3D Render", "Oil Painting"]} />
                                            <MetadataField label="Lens" value={activeShot.metadata.lens} onChange={(v) => updateShotMetadata('lens', v)} options={["None", "Macro Detail", "Close-Up", "Waist Up", "Full Body", "Wide View"]} />
                                            <MetadataField label="Angle" value={activeShot.metadata.angle} onChange={(v) => updateShotMetadata('angle', v)} options={["None", "Eye Level", "High Angle", "Low Angle", "Top Down"]} />
                                            <MetadataField label="Time of Day" value={activeShot.metadata.tod} onChange={(v) => updateShotMetadata('tod', v)} options={["None", "Golden Hour", "High Noon", "Blue Hour", "Midnight", "Sunrise"]} />
                                            <MetadataField label="Lighting" value={activeShot.metadata.lighting} onChange={(v) => updateShotMetadata('lighting', v)} options={["None", "Natural", "Cinematic", "Neon", "Studio"]} />
                                        </div>

                                        <div className="p-3 bg-white/5 rounded-lg border border-white/5">
                                            <div className="text-[10px] text-zinc-500 font-mono mb-1 uppercase">Generated Prompt Preview</div>
                                            <div className="text-xs text-zinc-300 italic">
                                                "{activeSceneData?.slug}. {activeShot.description} {activeShot.metadata.style !== 'None' ? `, ${activeShot.metadata.style} style` : ''}..."
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>
                ) : (
                    <div className="h-[300px] rounded-2xl border border-dashed border-zinc-800 bg-onyx/50 flex flex-col items-center justify-center gap-4">
                        <div className="w-16 h-16 rounded-2xl bg-zinc-900 border border-white/5 flex items-center justify-center">
                            <LayoutIcon className="w-8 h-8 text-zinc-700" />
                        </div>
                        <div className="text-center">
                            <h4 className="text-zinc-400 font-medium">No Project Selected</h4>
                            <p className="text-zinc-600 text-sm mt-1">Select a script from the dropdown to start building scenes.</p>
                        </div>
                    </div>
                )}
                </div>
            </div>
        </div>
    );
};

const MetadataField: React.FC<{ label: string, value: string, options: string[], onChange: (v: string) => void }> = ({ label, value, options, onChange }) => (
    <div className="space-y-1.5">
        <label className="text-xs text-zinc-400 font-medium">{label}</label>
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full bg-onyx border border-white/10 rounded-md px-3 py-2 text-xs text-zinc-200 outline-none focus:border-purple-500/50 hover:bg-white/5 transition-colors cursor-pointer"
        >
            {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
    </div>
);

const MultiSelectMetadataField: React.FC<{ label: string, value: string, options: string[], onChange: (v: string) => void, max: number }> = ({ label, value, options, onChange, max }) => {
    // Current selections assumed to be separated by " + "
    const selected = value === "None" || value === "" ? [] : value.split(" + ");

    const toggleSelection = (option: string) => {
        let newSelected = [...selected];
        if (newSelected.includes(option)) {
            newSelected = newSelected.filter(item => item !== option);
        } else {
            if (newSelected.length < max) {
                newSelected.push(option);
            }
        }

        onChange(newSelected.length === 0 ? "None" : newSelected.join(" + "));
    };

    return (
        <div className="space-y-1.5">
            <div className="flex justify-between items-center">
                <label className="text-xs text-zinc-400 font-medium">{label}</label>
                <span className="text-[10px] text-zinc-600">{selected.length}/{max}</span>
            </div>

            <div className="flex flex-wrap gap-2">
                {options.map(opt => {
                    const isSelected = selected.includes(opt);
                    const isDisabled = !isSelected && selected.length >= max;
                    return (
                        <button
                            key={opt}
                            onClick={() => toggleSelection(opt)}
                            disabled={isDisabled}
                            className={`
                                px-2 py-1 rounded text-[10px] font-medium border transition-all
                                ${isSelected
                                    ? 'bg-purple-500/20 border-purple-500 text-purple-200'
                                    : isDisabled
                                        ? 'bg-white/5 border-transparent text-zinc-600 cursor-not-allowed opacity-50'
                                        : 'bg-onyx border-white/10 text-zinc-400 hover:border-white/30 hover:text-white'}
                            `}
                        >
                            {opt}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const StoryboardCard: React.FC<{
    shotNumber: number;
    label: string;
    type: string;
    desc: string;
    img: string;
    active?: boolean;
    onClick?: () => void;
    onDelete?: () => void;
    onRenameClick?: (e: React.MouseEvent) => void;
}> = ({ shotNumber, label, type, desc, img, active, onClick, onDelete, onRenameClick }) => (
    <div
        onClick={onClick}
        className={`
            group bg-onyx border rounded-xl overflow-hidden transition-all cursor-pointer relative
            ${active
                ? 'border-purple-500 shadow-[0_0_20px_rgba(168,85,247,0.2)] scale-[1.02] z-10'
                : 'border-white/5 hover:border-white/20 hover:shadow-2xl hover:shadow-purple-900/10'}
        `}
    >
        <div className="aspect-video relative overflow-hidden bg-carbon">
            <img src={img} alt="Shot" className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity duration-500" />

            {/* Label Badge with Edit trigger */}
            <div className="absolute top-2 left-2 flex items-center gap-1 group/badge">
                <div className="px-2 py-0.5 bg-carbon/60 backdrop-blur-md rounded text-[10px] font-mono text-zinc-300 border border-white/10 uppercase">
                    {label}
                </div>
                <button
                    onClick={onRenameClick}
                    className="p-0.5 bg-carbon/60 rounded border border-white/10 text-zinc-400 hover:text-white opacity-0 group-hover/badge:opacity-100 transition-opacity"
                >
                    <PencilIcon className="w-2.5 h-2.5" />
                </button>
            </div>

            {/* Delete Button */}
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    if (onDelete) onDelete();
                }}
                className="absolute top-2 right-2 p-1.5 bg-carbon/60 hover:bg-red-500/80 text-white rounded opacity-0 group-hover:opacity-100 transition-all duration-200 z-20 backdrop-blur-md border border-white/10 hover:border-red-400/50"
                title="Remove Shot"
            >
                <XIcon className="w-3 h-3" />
            </button>

            {active && (
                <div className="absolute inset-0 border-4 border-purple-500/50 pointer-events-none" />
            )}
        </div>
        <div className="p-3">
            <div className="flex justify-between items-center mb-1">
                <span className={`text-[10px] font-bold uppercase tracking-wider ${active ? 'text-purple-400' : 'text-zinc-500 group-hover:text-purple-400'}`}>{type}</span>
                {active && <CheckIcon className="w-3 h-3 text-purple-500" />}
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed line-clamp-2">{desc}</p>
        </div>
    </div>
);
